<template>
  <div class="detail law-page">
    <div class="detail-banner">
      <div class="container">
        <h1>AI 出海法律顾问</h1>
        <div class="sub">企业出海法律与合规智能问答 · 外资准入 · 实体设立 · 用工税务 · 数据合规 · 风险提示</div>
      </div>
    </div>

    <div class="detail-body">
      <div class="container">
        <span class="detail-back" @click="$goBack()">‹ 返回</span>

        <!-- ============ 醒目免责提醒（页面首屏固定位置） ============ -->
        <div class="law-alert">
          <div class="law-alert-icon">⚠️</div>
          <div class="law-alert-body">
            <div class="law-alert-title">重要提示：所有输出结果仅为建议</div>
            <div class="law-alert-text">
              本页面由 AI 基于公开资料生成，<strong>仅供参考，不构成法律意见</strong>，也不能替代执业律师或持牌机构的正式意见。
              任何事项正式落地前，请务必委托<strong>目标国家/区域的执业律师、会计师及合规顾问</strong>进行专项核验。
            </div>
          </div>
        </div>

        <!-- ============ 功能介绍 ============ -->
        <div class="law-intro">
          <div class="law-intro-head">
            <div class="section-title">这个页面能帮你解答什么</div>
            <p class="law-intro-desc">
              围绕「企业出海」的法律与合规问题，结合你的<strong>目标国家/区域</strong>与<strong>所属行业</strong>，
              梳理外国公司进入当地的法律框架、风险点与前中后期需要办理的法律事项，并给出可追溯的资料出处。
            </p>
          </div>
          <div class="law-cap-grid">
            <div class="law-cap" v-for="c in capabilities" :key="c.title">
              <div class="law-cap-icon">{{ c.icon }}</div>
              <h3>{{ c.title }}</h3>
              <p>{{ c.desc }}</p>
            </div>
          </div>
        </div>

        <!-- ============ 对话主体 ============ -->
        <div class="law-main">
          <!-- 左：聊天区 -->
          <div class="law-chat">
            <div class="law-chat-head">
              <div class="law-chat-title">法律咨询对话</div>
              <span class="law-restart" @click="restart">重新开始</span>
            </div>

            <div class="law-msgs" ref="scroll">
              <div
                v-for="m in messages"
                :key="m.id"
                class="law-msg"
                :class="'is-' + m.role"
              >
                <div class="law-avatar" v-if="m.role !== 'user'">{{ avatarOf(m.role) }}</div>
                <div class="law-bubble">
                  <div class="law-bubble-tag" v-if="m.role === 'guide'">补充信息</div>
                  <div class="law-bubble-tag warn" v-if="m.role === 'reject'">超出受理范围</div>
                  <div class="law-bubble-tag warn" v-if="m.role === 'error'">服务提示</div>
                  <div class="law-bubble-tag" v-if="m.role === 'notice'">系统提示</div>
                  <div class="law-md" v-html="m.html"></div>
                  <span class="law-caret" v-if="m.streaming"></span>
                  <div class="law-bubble-foot" v-if="m.role === 'ai' && !m.streaming && m.text">
                    <span class="law-foot-warn">以上内容仅为建议，落地请咨询专业机构</span>
                  </div>
                </div>
                <div class="law-avatar user" v-if="m.role === 'user'">我</div>
              </div>

              <!-- 思考中提醒 -->
              <div class="law-msg is-ai" v-if="phase === 'analyzing' || phase === 'thinking'">
                <div class="law-avatar">法</div>
                <div class="law-bubble thinking">
                  <span class="dots"><i></i><i></i><i></i></span>
                  <span class="thinking-text">{{ thinkingText }}</span>
                </div>
              </div>
            </div>

            <!-- 快捷选项 -->
            <div class="law-picker" v-if="pendingOptions">
              <div class="law-picker-title">{{ pendingOptions.title }}</div>
              <div class="law-chips">
                <span
                  v-for="opt in pendingOptions.items"
                  :key="opt.value"
                  class="law-chip"
                  @click="pickOption(pendingOptions.key, opt)"
                >{{ opt.label }}</span>
              </div>
            </div>

            <!-- 输入区 -->
            <div class="law-input">
              <textarea
                v-model="input"
                :placeholder="placeholder"
                rows="3"
                @keydown.enter.exact.prevent="send"
              ></textarea>
              <div class="law-input-foot">
                <span class="law-input-tip">回车发送 / Shift+回车换行 · 输出仅为建议，不构成法律意见</span>
                <div class="law-btns">
                  <button class="btn-ghost" v-if="busy" @click="stop">停止生成</button>
                  <button class="btn-primary" :disabled="busy" @click="send">
                    {{ busy ? '生成中…' : '发送' }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- 右：四要素面板 -->
          <aside class="law-side">
            <div class="law-side-card">
              <div class="law-side-title">本次咨询要素</div>
              <p class="law-side-desc">四项信息齐全后才会输出分析结果，缺失项会在对话中引导补充。</p>

              <div class="law-slot" v-for="s in slotView" :key="s.key">
                <div class="law-slot-head">
                  <span class="law-slot-name">{{ s.name }}</span>
                  <span class="law-slot-state" :class="s.value ? 'done' : 'todo'">{{ s.value ? '已填写' : '待补充' }}</span>
                </div>
                <div class="law-slot-value" v-if="s.value">{{ s.value }}</div>
                <div class="law-slot-empty" v-else>{{ s.hint }}</div>
                <div class="law-slot-ops" v-if="s.value">
                  <span @click="resetSlot(s.key)">更换</span>
                </div>
              </div>

              <div class="law-side-block">
                <div class="law-side-sub">公司规模（点击选择）</div>
                <div class="law-chips small">
                  <span
                    v-for="opt in scaleOptions"
                    :key="opt.value"
                    class="law-chip"
                    :class="{ active: slots.scale === opt.value }"
                    @click="pickOption('scale', opt)"
                  >{{ opt.label }}</span>
                </div>
              </div>

              <div class="law-side-block" v-if="samples.length">
                <div class="law-side-sub">试试这样问</div>
                <div class="law-sample" v-for="s in samples" :key="s" @click="useSample(s)">{{ s }}</div>
              </div>
            </div>

            <div class="law-side-card note">
              <div class="law-side-title">落地务必找专业机构</div>
              <ul class="law-note-list">
                <li>目标国投资促进机构 / 公司注册局</li>
                <li>中国驻当地使领馆经济商务处</li>
                <li>中国贸促会及地方分会</li>
                <li>目标市场执业律师事务所</li>
                <li>具备当地资质的会计师事务所</li>
              </ul>
              <div class="law-note-tip">{{ disclaimer }}</div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { fetchLawMeta, analyzeLawInput, streamLawAnswer } from '../api/lawAi'

// ---------- 轻量 Markdown 渲染（先转义，再做有限的标记替换，避免 XSS） ----------
function escapeHtml (str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

function inlineMd (str) {
  return str
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/(https?:\/\/[^\s，。、）)"']+)/g, '<a href="$1" target="_blank" rel="noopener">$1</a>')
}

function renderMd (text) {
  if (!text) return ''
  const lines = escapeHtml(text).split('\n')
  let html = ''
  let inUl = false
  const closeUl = () => {
    if (inUl) {
      html += '</ul>'
      inUl = false
    }
  }
  lines.forEach(raw => {
    const line = raw.trim()
    if (!line) {
      closeUl()
      return
    }
    if (/^####\s+/.test(line)) {
      closeUl()
      html += '<h5>' + inlineMd(line.replace(/^####\s+/, '')) + '</h5>'
    } else if (/^###\s+/.test(line)) {
      closeUl()
      html += '<h4>' + inlineMd(line.replace(/^###\s+/, '')) + '</h4>'
    } else if (/^#{1,2}\s+/.test(line)) {
      closeUl()
      html += '<h3>' + inlineMd(line.replace(/^#{1,2}\s+/, '')) + '</h3>'
    } else if (/^[-*•]\s+/.test(line)) {
      if (!inUl) {
        html += '<ul>'
        inUl = true
      }
      html += '<li>' + inlineMd(line.replace(/^[-*•]\s+/, '')) + '</li>'
    } else if (/^\d+[.、)）]\s*/.test(line)) {
      // 保留模型自带编号，避免 <ol> 二次编号
      closeUl()
      html += '<p class="md-num">' + inlineMd(line) + '</p>'
    } else {
      closeUl()
      html += '<p>' + inlineMd(line) + '</p>'
    }
  })
  closeUl()
  return html
}

const DEFAULT_SCALES = [
  { value: 'startup', label: '初创/小微企业（100人以下）' },
  { value: 'growth', label: '成长型企业（100-1000人）' },
  { value: 'large', label: '大型企业（1000人以上）' }
]

const THINKING_STAGES = [
  '正在解析您的咨询要素…',
  '正在梳理目标市场的外资准入规则…',
  '正在核对法规名称与主管机构出处…',
  '正在整理前期 / 中期 / 后期法律事项…',
  '正在校验内容是否限定在法律范围内…'
]

// 命中这些关键词的提示属内部过程（要素变更、模型切换、重试等），一律不展示给用户
const INTERNAL_NOTICE_WORDS = [
  '已更新',
  '已自动切换',
  '切换至',
  '重新生成',
  '重试',
  '模型',
  '槽位',
  '已清空'
]

const SLOT_META = [
  { key: 'question', name: '主要问题', hint: '例如：设立子公司需要哪些许可与法律文件' },
  { key: 'country', name: '目标国家 / 区域', hint: '例如：越南、阿联酋、欧盟' },
  { key: 'industry', name: '所属行业', hint: '例如：跨境电商、汽车及零部件' },
  { key: 'scale', name: '公司规模', hint: '初创/小微、成长型、大型' }
]

export default {
  name: 'AiLaw',
  data () {
    return {
      sessionId: 'law_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7),
      capabilities: [
        { icon: '🏛', title: '外资准入与实体设立', desc: '负面清单、允许的实体形式与持股比例、注册登记流程与主管机关。' },
        { icon: '📜', title: '行业许可与强制认证', desc: '行业专项牌照、产品准入认证、标签与广告等合规要求。' },
        { icon: '👥', title: '用工 · 税务 · 数据合规', desc: '劳动法与雇佣风险、签证与工作许可、税收协定、数据跨境与隐私。' },
        { icon: '🛡', title: '风险防控与争议解决', desc: '知识产权、制裁与出口管制、合同安排、争议解决与退出清算。' }
      ],
      samples: [
        '我们想在越南设厂，需要办理哪些法律手续？',
        '跨境电商进入欧盟有哪些数据合规要求？',
        '在阿联酋设立子公司，外资持股有限制吗？'
      ],
      scaleOptions: DEFAULT_SCALES.slice(),
      countryOptions: [],
      industryOptions: [],
      thinkingDelayMs: 5000,
      disclaimer: '本页面所有输出均为参考建议，不构成法律意见，落地前请由目标市场执业律师与持牌专业机构出具正式意见。',
      slots: { question: '', country: '', industry: '', scale: '', scaleLabel: '' },
      messages: [],
      pendingOptions: null,
      input: '',
      phase: 'idle', // idle | analyzing | thinking | streaming
      thinkingText: THINKING_STAGES[0],
      thinkingIdx: 0,
      thinkingTimer: null,
      typeStartTimer: null,
      typeTimer: null,
      buffer: '',
      streamEnded: false,
      streamCtl: null,
      askStart: 0,
      currentId: '',
      pendingError: '',
      seq: 0,
      // 多轮上下文：已完成轮次的问答（{ role: 'user' | 'assistant', content }），提问时回传给后端，
      // 使「那如果是德国呢」「规模改成大型」这类追问能被正确理解。
      history: [],
      // 本轮用户输入的原始文本，答案生成完成后连同回答一起写入 history
      pendingUserText: '',
      // 用户主动「停止生成」时跳过写入上下文，避免残缺答案污染后续追问
      skipHistory: false,
      // 点「更换」清空要素时暂存的旧值 { key, old }，待选新值后同步替换问题描述里的旧要素
      pendingReplace: null
    }
  },
  computed: {
    busy () {
      return this.phase !== 'idle'
    },
    placeholder () {
      return '请描述你的出海法律问题，并包含：目标国家/区域、所属行业、公司规模。\n例如：我们是 300 人的汽车零部件公司，想在墨西哥设厂，需要办理哪些法律手续？'
    },
    slotView () {
      return SLOT_META.map(m => ({
        key: m.key,
        name: m.name,
        hint: m.hint,
        value: m.key === 'scale' ? this.slots.scaleLabel : this.slots[m.key]
      }))
    }
  },
  mounted () {
    this.greet()
    this.loadMeta()
  },
  beforeDestroy () {
    this.clearTimers()
    if (this.streamCtl) this.streamCtl.abort()
  },
  methods: {
    async loadMeta () {
      try {
        const meta = await fetchLawMeta()
        if (meta.scaleOptions && meta.scaleOptions.length) this.scaleOptions = meta.scaleOptions
        if (meta.countryOptions) this.countryOptions = meta.countryOptions
        if (meta.industryOptions) this.industryOptions = meta.industryOptions
        if (meta.thinkingDelayMs) this.thinkingDelayMs = meta.thinkingDelayMs
        if (meta.disclaimer) this.disclaimer = meta.disclaimer
      } catch (e) {
        // 元数据失败不阻塞使用，沿用本地默认选项
      }
    },
    greet () {
      this.pushMsg('ai', [
        '你好，我是**企业出海法律顾问助手**，只回答与出海相关的**法律、法规与合规**问题。',
        '',
        '为了给出准确、可追溯的分析，请在提问中包含以下四项信息：',
        '1. 主要问题内容',
        '2. 要去的目标国家或区域',
        '3. 公司所属行业',
        '4. 公司大体规模（初创/小微 100 人以下、成长型 100-1000 人、大型 1000 人以上）'
      ].join('\n'))
    },
    avatarOf (role) {
      if (role === 'guide') return '?'
      if (role === 'reject') return '!'
      if (role === 'error' || role === 'notice') return 'i'
      return '法'
    },
    pushMsg (role, text) {
      this.seq += 1
      const msg = {
        id: role + '_' + this.seq,
        role,
        text: text || '',
        html: renderMd(text || ''),
        streaming: false,
        model: ''
      }
      this.messages.push(msg)
      this.scrollBottom()
      return msg
    },
    findMsg (id) {
      for (let i = 0; i < this.messages.length; i++) {
        if (this.messages[i].id === id) return this.messages[i]
      }
      return null
    },
    scrollBottom () {
      this.$nextTick(() => {
        const el = this.$refs.scroll
        if (el) el.scrollTop = el.scrollHeight
      })
    },
    useSample (text) {
      if (this.busy) return
      this.input = text
    },
    resetSlot (key) {
      if (this.busy) return
      // 暂存被清空的旧值：用户选定新值后同步替换问题描述里的旧要素，
      // 避免「目标国家已改成泰国、问题里仍写越南」这类自相矛盾的提问
      const old = key === 'scale' ? this.slots.scaleLabel : this.slots[key]
      this.pendingReplace = { key: key, old: old || '' }
      this.slots[key] = ''
      if (key === 'scale') this.slots.scaleLabel = ''
      // 清空后直接弹出选项供重新选择，不再输出「已清空…」类系统提示
      this.showPicker([key])
    },
    restart () {
      if (this.streamCtl) this.streamCtl.abort()
      this.clearTimers()
      this.slots = { question: '', country: '', industry: '', scale: '', scaleLabel: '' }
      this.messages = []
      this.history = []
      this.pendingUserText = ''
      this.skipHistory = false
      this.pendingReplace = null
      this.pendingOptions = null
      this.input = ''
      this.buffer = ''
      this.phase = 'idle'
      this.greet()
    },
    // ---------- 选项 ----------
    showPicker (missing, options) {
      const key = (missing || []).filter(k => k !== 'question')[0]
      if (!key) {
        this.pendingOptions = null
        return
      }
      let items = options && options[key] ? options[key] : null
      if (!items) {
        if (key === 'scale') items = this.scaleOptions
        else if (key === 'country') items = this.countryOptions
        else if (key === 'industry') items = this.industryOptions
      }
      if (!items || !items.length) {
        this.pendingOptions = null
        return
      }
      const titleMap = {
        country: '选择目标国家 / 区域（也可直接输入其他名称）',
        industry: '选择所属行业（也可直接输入其他行业）',
        scale: '选择公司规模'
      }
      this.pendingOptions = { key, title: titleMap[key] || '请选择', items }
      this.scrollBottom()
    },
    pickOption (key, opt) {
      if (this.busy) return
      this.slots[key] = opt.value
      if (key === 'scale') this.slots.scaleLabel = opt.label
      // 若此前点过「更换」，把问题描述里残留的旧要素替换成新值，保持要素与描述一致
      const rep = this.pendingReplace
      if (rep && rep.key === key && rep.old && this.slots.question) {
        const nv = key === 'scale' ? opt.label : opt.value
        this.slots.question = this.slots.question.split(rep.old).join(nv)
      }
      this.pendingReplace = null
      this.pushMsg('user', opt.label)
      this.pendingOptions = null
      this.pendingUserText = opt.label
      this.flow('')
    },
    // ---------- 主流程 ----------
    send () {
      if (this.busy) return
      const text = this.input.trim()
      if (!text) {
        this.$toast.show({ message: '请先输入你的法律问题', type: 'error' })
        return
      }
      this.pushMsg('user', text)
      this.input = ''
      this.pendingOptions = null
      this.pendingReplace = null
      this.pendingUserText = text
      this.flow(text)
    },
    async flow (inputText) {
      this.phase = 'analyzing'
      this.thinkingText = '正在解析您的咨询要素…'
      this.startThinking()
      try {
        const res = await analyzeLawInput({
          sessionId: this.sessionId,
          input: inputText,
          question: this.slots.question,
          country: this.slots.country,
          industry: this.slots.industry,
          scale: this.slots.scale
        })
        if (res.slots) this.applySlots(res.slots)

        // 后续轮次调整国家 / 行业 / 规模属内部要素变更，不在对话中提示，直接按新要素重新作答
        if (res.legal === false) {
          this.stopThinking()
          this.phase = 'idle'
          this.pushMsg('reject', res.rejectMessage || '该问题超出本页面的受理范围。')
          return
        }
        if (!res.ready) {
          this.stopThinking()
          this.phase = 'idle'
          this.pushMsg('guide', res.message || '请补充完整信息后再提交。')
          this.showPicker(res.missing, res.options)
          return
        }
        await this.ask()
      } catch (e) {
        this.stopThinking()
        this.phase = 'idle'
        this.pushMsg('error', '请求失败：' + ((e && e.message) || '网络异常') + '。请稍后重试。')
      }
    },
    applySlots (slots) {
      this.slots = {
        question: slots.question || '',
        country: slots.country || '',
        industry: slots.industry || '',
        scale: slots.scale || '',
        scaleLabel: slots.scaleLabel || ''
      }
    },
    ask () {
      this.phase = 'thinking'
      this.buffer = ''
      this.streamEnded = false
      this.pendingError = ''
      this.askStart = Date.now()
      this.thinkingIdx = 0
      this.thinkingText = THINKING_STAGES[0]
      this.startThinking()
      this.currentId = ''

      const ctl = streamLawAnswer({
        sessionId: this.sessionId,
        input: '',
        question: this.slots.question,
        country: this.slots.country,
        industry: this.slots.industry,
        scale: this.slots.scale,
        // 带上之前轮次的问答，让模型理解追问与参数变更的语境
        history: this.history.slice()
      }, {
        meta: d => {
          if (d && d.thinkingDelayMs) this.thinkingDelayMs = d.thinkingDelayMs
        },
        thinking: d => {
          if (d && d.stage && this.phase === 'thinking') this.thinkingText = d.stage
        },
        notice: d => {
          if (!d || !d.message) return
          // 内部过程（要素变更、主备模型切换、重试等）不在前端展示，用户无需感知
          if (INTERNAL_NOTICE_WORDS.some(w => d.message.indexOf(w) !== -1)) return
          this.pushMsg('notice', d.message)
        },
        reset: () => {
          // 后端在单个模型中途失败后静默切换到下一个模型：移除残留气泡，回到思考态只保留一个执行中提示
          this.clearTimers()
          const msg = this.findMsg(this.currentId)
          if (msg) {
            this.messages = this.messages.filter(m => m.id !== msg.id)
          }
          this.currentId = ''
          this.buffer = ''
          this.phase = 'thinking'
          this.thinkingText = '正在生成法律要点与出处…'
        },
        guide: d => {
          this.dropCurrentIfEmpty()
          this.pushMsg('guide', (d && d.message) || '请补充完整信息。')
          if (d && d.slots) this.applySlots(d.slots)
          this.showPicker(d && d.missing, d && d.options)
        },
        reject: d => {
          this.dropCurrentIfEmpty()
          this.pushMsg('reject', (d && d.message) || '该问题超出本页面的受理范围。')
        },
        delta: d => {
          if (!d || !d.text) return
          this.buffer += d.text
          this.scheduleTyping()
        },
        error: d => {
          this.pendingError = (d && d.text) || '生成过程出现异常'
        },
        done: d => {
          if (d && d.disclaimer) this.disclaimer = d.disclaimer
          // 收到 done 事件即标记结束，避免依赖 HTTP 连接关闭时机导致按钮卡在「生成中」
          this.streamEnded = true
          if (!this.typeTimer && !this.typeStartTimer) this.finish()
        }
      })
      this.streamCtl = ctl

      return ctl.done
        .catch(e => {
          if (e && e.name === 'AbortError') return
          this.pendingError = '连接中断：' + ((e && e.message) || '网络异常')
        })
        .then(() => {
          this.streamEnded = true
          // 没有任何正文（拒答/引导/异常）时立即收尾
          if (!this.typeTimer && !this.typeStartTimer) this.finish()
        })
    },
    // ---------- 思考中提醒 ----------
    startThinking () {
      this.stopThinking()
      this.thinkingTimer = setInterval(() => {
        this.thinkingIdx = (this.thinkingIdx + 1) % THINKING_STAGES.length
        this.thinkingText = THINKING_STAGES[this.thinkingIdx]
      }, 1800)
    },
    stopThinking () {
      if (this.thinkingTimer) {
        clearInterval(this.thinkingTimer)
        this.thinkingTimer = null
      }
    },
    // ---------- 打字机输出（先满足最短思考提示时长） ----------
    scheduleTyping () {
      if (this.typeTimer || this.typeStartTimer) return
      const wait = Math.max(0, this.thinkingDelayMs - (Date.now() - this.askStart))
      this.typeStartTimer = setTimeout(() => {
        this.typeStartTimer = null
        this.beginTyping()
      }, wait)
    },
    beginTyping () {
      this.stopThinking()
      this.phase = 'streaming'
      // 首次出字时才创建 AI 气泡，避免在「思考中」阶段出现一个空闪烁框
      if (!this.currentId) {
        const msg = this.pushMsg('ai', '')
        msg.streaming = true
        this.currentId = msg.id
      }
      if (this.typeTimer) return
      this.typeTimer = setInterval(this.tick, 32)
    },
    tick () {
      const msg = this.findMsg(this.currentId)
      if (!msg) {
        this.finish()
        return
      }
      if (this.buffer.length) {
        // 缓冲越多吐字越快，保证不落后于模型
        const step = Math.max(2, Math.ceil(this.buffer.length / 30))
        msg.text += this.buffer.slice(0, step)
        this.buffer = this.buffer.slice(step)
        msg.html = renderMd(msg.text)
        this.scrollBottom()
      } else if (this.streamEnded) {
        this.finish()
      }
    },
    finish () {
      this.clearTimers()
      const msg = this.findMsg(this.currentId)
      if (msg) {
        msg.streaming = false
        if (!msg.text) {
          this.messages = this.messages.filter(m => m.id !== msg.id)
        } else {
          msg.html = renderMd(msg.text)
        }
      }
      // 本轮问答写入上下文，供下一轮追问使用（拒答 / 引导等无正文场景只记录提问，保持语境连贯）
      this.commitHistory(msg && msg.text ? msg.text : '')
      if (this.pendingError) {
        this.pushMsg('error', this.pendingError)
        this.pendingError = ''
      }
      this.currentId = ''
      this.phase = 'idle'
      this.scrollBottom()
    },
    commitHistory (answer) {
      // 用户主动停止生成时不写入，避免残缺答案污染后续追问
      if (this.skipHistory) {
        this.skipHistory = false
        this.pendingUserText = ''
        return
      }
      if (this.pendingUserText) {
        this.history.push({ role: 'user', content: this.pendingUserText })
        this.pendingUserText = ''
      }
      if (answer) {
        this.history.push({ role: 'assistant', content: answer })
      }
      // 只保留最近若干条，避免请求体过大
      if (this.history.length > 16) {
        this.history = this.history.slice(this.history.length - 16)
      }
    },
    dropCurrentIfEmpty () {
      const msg = this.findMsg(this.currentId)
      if (msg && !msg.text) {
        this.messages = this.messages.filter(m => m.id !== msg.id)
        this.currentId = ''
      }
    },
    stop () {
      if (this.streamCtl) this.streamCtl.abort()
      this.streamEnded = true
      this.buffer = ''
      const msg = this.findMsg(this.currentId)
      if (msg && msg.text) msg.text += '\n\n（已停止生成，以上内容可能不完整）'
      // 主动停止：残缺答案不写入多轮上下文，避免污染后续追问
      this.skipHistory = true
      this.finish()
    },
    clearTimers () {
      this.stopThinking()
      if (this.typeStartTimer) {
        clearTimeout(this.typeStartTimer)
        this.typeStartTimer = null
      }
      if (this.typeTimer) {
        clearInterval(this.typeTimer)
        this.typeTimer = null
      }
    }
  }
}
</script>

<style scoped>
/* ============ 免责提醒 ============ */
.law-alert {
  display: flex;
  gap: 14px;
  align-items: flex-start;
  background: linear-gradient(135deg, #fff7e6, #fffdf7);
  border: 1px solid #ffd591;
  border-left: 5px solid #fa8c16;
  border-radius: 12px;
  padding: 18px 20px;
  margin-bottom: 24px;
}
.law-alert-icon {
  font-size: 22px;
  line-height: 1.2;
}
.law-alert-title {
  font-size: 16px;
  font-weight: 800;
  color: #ad4e00;
  margin-bottom: 6px;
}
.law-alert-text {
  font-size: 13.5px;
  color: #7c5100;
  line-height: 1.8;
}

/* ============ 功能介绍 ============ */
.law-intro {
  background: var(--white);
  border: 1px solid var(--border);
  border-radius: 16px;
  padding: 26px;
  box-shadow: var(--shadow);
  margin-bottom: 24px;
}
.law-intro-head .section-title {
  font-size: 22px;
  margin-bottom: 12px;
}
.law-intro-desc {
  font-size: 14px;
  color: var(--text-sub);
  line-height: 1.85;
  margin-bottom: 20px;
}
.law-cap-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 20px;
}
.law-cap {
  background: linear-gradient(160deg, #ffffff, var(--blue-soft-2));
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 18px;
}
.law-cap-icon {
  font-size: 24px;
  margin-bottom: 8px;
}
.law-cap h3 {
  font-size: 15.5px;
  color: var(--primary);
  margin-bottom: 8px;
}
.law-cap p {
  font-size: 13px;
  color: var(--text-sub);
  line-height: 1.75;
}
.law-scope {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}
.law-scope-item {
  font-size: 13px;
  line-height: 1.8;
  padding: 12px 14px;
  border-radius: 10px;
  background: var(--blue-soft);
  color: var(--text-main);
}
.law-scope-item .tag {
  display: inline-block;
  font-size: 12px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 6px;
  margin-right: 8px;
  background: var(--primary);
  color: #fff;
}
.law-scope-item.no {
  background: #fff1f0;
}
.law-scope-item.no .tag {
  background: #cf1322;
}
.law-scope-item.warn {
  background: #fffbe6;
}
.law-scope-item.warn .tag {
  background: #d48806;
}

/* ============ 主体布局 ============ */
.law-main {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: 20px;
  align-items: start;
}

/* ============ 聊天区 ============ */
.law-chat {
  background: var(--white);
  border: 1px solid var(--border);
  border-radius: 16px;
  box-shadow: var(--shadow);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.law-chat-head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid var(--border);
  background: linear-gradient(135deg, var(--blue-soft-2), #fff);
}
.law-chat-title {
  font-size: 16px;
  font-weight: 800;
  color: var(--text-main);
}
.law-chat-model {
  font-size: 12px;
  color: var(--text-sub);
  background: var(--blue-soft);
  padding: 3px 10px;
  border-radius: 20px;
}
.law-restart {
  margin-left: auto;
  font-size: 13px;
  color: var(--primary);
  cursor: pointer;
}
.law-restart:hover {
  text-decoration: underline;
}
.law-msgs {
  height: 520px;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: #fbfcff;
}
.law-msg {
  display: flex;
  gap: 10px;
  align-items: flex-start;
}
.law-msg.is-user {
  justify-content: flex-end;
}
.law-avatar {
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border-radius: 10px;
  background: linear-gradient(135deg, var(--primary-light), var(--primary));
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}
.law-avatar.user {
  background: linear-gradient(135deg, #8c9ab8, #5b6b8c);
}
.law-bubble {
  max-width: calc(100% - 60px);
  background: #fff;
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 14px 16px;
  font-size: 14px;
  line-height: 1.85;
  color: var(--text-main);
  box-shadow: 0 2px 10px rgba(36, 68, 203, 0.05);
}
.is-user .law-bubble {
  background: var(--primary);
  border-color: var(--primary);
  color: #fff;
}
.is-guide .law-bubble {
  background: #f6ffed;
  border-color: #b7eb8f;
}
.is-reject .law-bubble {
  background: #fff1f0;
  border-color: #ffa39e;
}
.is-error .law-bubble,
.is-notice .law-bubble {
  background: #fffbe6;
  border-color: #ffe58f;
}
.law-bubble-tag {
  display: inline-block;
  font-size: 12px;
  font-weight: 700;
  color: #389e0d;
  margin-bottom: 6px;
}
.law-bubble-tag.warn {
  color: #cf1322;
}
.law-bubble-foot {
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px dashed var(--border);
  font-size: 12px;
  color: #9aa6c0;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}
.law-foot-warn {
  color: #d48806;
}
.law-bubble.thinking {
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--text-sub);
}
.thinking-text {
  font-size: 13.5px;
}
.dots {
  display: inline-flex;
  gap: 4px;
}
.dots i {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--primary);
  animation: blink 1.2s infinite ease-in-out;
}
.dots i:nth-child(2) {
  animation-delay: 0.2s;
}
.dots i:nth-child(3) {
  animation-delay: 0.4s;
}
@keyframes blink {
  0%, 80%, 100% { opacity: 0.25; transform: translateY(0); }
  40% { opacity: 1; transform: translateY(-3px); }
}
.law-caret {
  display: inline-block;
  width: 7px;
  height: 15px;
  background: var(--primary);
  vertical-align: -2px;
  animation: caret 0.9s steps(1) infinite;
}
@keyframes caret {
  50% { opacity: 0; }
}

/* markdown 正文 */
.law-md >>> h3 {
  font-size: 16px;
  font-weight: 800;
  color: var(--primary);
  margin: 16px 0 8px;
  padding-left: 10px;
  border-left: 4px solid var(--primary);
}
.law-md >>> h4 {
  font-size: 14.5px;
  font-weight: 700;
  color: var(--text-main);
  margin: 12px 0 6px;
}
.law-md >>> h5 {
  font-size: 14px;
  font-weight: 700;
  color: var(--text-sub);
  margin: 10px 0 6px;
}
.law-md >>> p {
  margin: 6px 0;
}
.law-md >>> p.md-num {
  padding-left: 2px;
}
.law-md >>> ul {
  margin: 6px 0 6px 18px;
}
.law-md >>> li {
  margin: 4px 0;
}
.law-md >>> code {
  background: var(--blue-soft);
  padding: 1px 5px;
  border-radius: 4px;
  font-size: 13px;
}
.law-md >>> a {
  color: var(--primary);
  word-break: break-all;
}
.is-user .law-md >>> a {
  color: #fff;
}

/* ============ 快捷选项 ============ */
.law-picker {
  padding: 14px 20px;
  border-top: 1px solid var(--border);
  background: var(--blue-soft-2);
}
.law-picker-title {
  font-size: 13px;
  font-weight: 700;
  color: var(--text-main);
  margin-bottom: 10px;
}
.law-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.law-chip {
  font-size: 13px;
  padding: 6px 12px;
  border-radius: 20px;
  background: #fff;
  border: 1px solid #d6e0ff;
  color: var(--primary);
  cursor: pointer;
  transition: all 0.2s ease;
}
.law-chip:hover {
  background: var(--primary);
  color: #fff;
  border-color: var(--primary);
}
.law-chip.active {
  background: var(--primary);
  color: #fff;
  border-color: var(--primary);
}
.law-chips.small .law-chip {
  font-size: 12.5px;
  padding: 5px 10px;
}

/* ============ 输入区 ============ */
.law-input {
  border-top: 1px solid var(--border);
  padding: 14px 20px 18px;
  background: #fff;
}
.law-input textarea {
  width: 100%;
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 12px 14px;
  font-size: 14px;
  line-height: 1.7;
  color: var(--text-main);
  resize: vertical;
  outline: none;
  font-family: inherit;
  background: #fbfcff;
}
.law-input textarea:focus {
  border-color: var(--primary-light);
  background: #fff;
}
.law-input-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 10px;
}
.law-input-tip {
  font-size: 12px;
  color: #9aa6c0;
}
.law-btns {
  display: flex;
  gap: 10px;
}
.btn-ghost {
  padding: 9px 16px;
  border-radius: 8px;
  border: 1px solid #d6e0ff;
  background: #fff;
  color: var(--primary);
  font-size: 14px;
  cursor: pointer;
}
.btn-ghost:hover {
  background: var(--blue-soft);
}

/* ============ 右侧要素面板 ============ */
.law-side {
  display: flex;
  flex-direction: column;
  gap: 16px;
  position: sticky;
  top: 20px;
}
.law-side-card {
  background: var(--white);
  border: 1px solid var(--border);
  border-radius: 16px;
  padding: 20px;
  box-shadow: var(--shadow);
}
.law-side-card.note {
  background: linear-gradient(160deg, #fffdf7, #fff);
  border-color: #ffe58f;
}
.law-side-title {
  font-size: 15.5px;
  font-weight: 800;
  color: var(--text-main);
  margin-bottom: 8px;
}
.law-side-desc {
  font-size: 12.5px;
  color: var(--text-sub);
  line-height: 1.7;
  margin-bottom: 14px;
}
.law-slot {
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 10px 12px;
  margin-bottom: 10px;
  background: #fbfcff;
}
.law-slot-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}
.law-slot-name {
  font-size: 13px;
  font-weight: 700;
  color: var(--text-main);
}
.law-slot-state {
  font-size: 11.5px;
  padding: 1px 7px;
  border-radius: 10px;
}
.law-slot-state.done {
  background: #f6ffed;
  color: #389e0d;
  border: 1px solid #b7eb8f;
}
.law-slot-state.todo {
  background: #fff1f0;
  color: #cf1322;
  border: 1px solid #ffa39e;
}
.law-slot-value {
  font-size: 13px;
  color: var(--text-main);
  line-height: 1.6;
  word-break: break-all;
}
.law-slot-empty {
  font-size: 12px;
  color: #9aa6c0;
  line-height: 1.6;
}
.law-slot-ops {
  margin-top: 6px;
  font-size: 12px;
  color: var(--primary);
  cursor: pointer;
}
.law-side-block {
  margin-top: 16px;
}
.law-side-sub {
  font-size: 13px;
  font-weight: 700;
  color: var(--text-main);
  margin-bottom: 8px;
}
.law-sample {
  font-size: 12.5px;
  color: var(--text-sub);
  line-height: 1.6;
  padding: 8px 10px;
  border: 1px dashed #d6e0ff;
  border-radius: 8px;
  margin-bottom: 8px;
  cursor: pointer;
}
.law-sample:hover {
  background: var(--blue-soft);
  color: var(--primary);
}
.law-note-list {
  margin: 0 0 12px 18px;
  padding: 0;
}
.law-note-list li {
  font-size: 12.5px;
  color: var(--text-sub);
  line-height: 1.9;
}
.law-note-tip {
  font-size: 12px;
  color: #ad6800;
  line-height: 1.75;
  background: #fffbe6;
  border: 1px solid #ffe58f;
  border-radius: 8px;
  padding: 10px;
}

/* ============ 响应式 ============ */
@media (max-width: 1024px) {
  .law-main {
    grid-template-columns: 1fr;
  }
  .law-side {
    position: static;
  }
  .law-cap-grid {
    grid-template-columns: 1fr 1fr;
  }
  .law-scope {
    grid-template-columns: 1fr;
  }
}
@media (max-width: 560px) {
  .law-cap-grid {
    grid-template-columns: 1fr;
  }
  .law-msgs {
    height: 60vh;
    padding: 14px;
  }
  .law-bubble {
    max-width: calc(100% - 46px);
  }
}
</style>
